// Cloud "database" for Daily Tickets, backed by Supabase (PostgREST).
//
// Tickets are stored in a shared Supabase table so they can be created on one
// device (e.g. a phone in the truck) and reopened/edited on another (the office
// PC). We talk to Supabase's auto-generated REST endpoint directly with fetch,
// so no client library / build dependency is required.
//
// A localStorage copy is kept as an OFFLINE CACHE: reads fall back to it when
// the network is unavailable, and every successful cloud read refreshes it.
// Signatures are stored as their cropped PNG data URLs (plain strings), which
// serialize cleanly to JSON / JSONB.
//
// ── One-time Supabase setup (run once in the Supabase SQL editor) ────────────
//   create table if not exists public.daily_tickets (
//     id text primary key,
//     data jsonb not null,
//     updated_at timestamptz not null default now()
//   );
//   alter table public.daily_tickets enable row level security;
//   -- Public access (no auth) so any device with the app can read/write:
//   create policy "public read"   on public.daily_tickets for select using (true);
//   create policy "public insert" on public.daily_tickets for insert with check (true);
//   create policy "public update" on public.daily_tickets for update using (true) with check (true);
//   create policy "public delete" on public.daily_tickets for delete using (true);
// ─────────────────────────────────────────────────────────────────────────────

const SUPABASE_URL = 'https://czpephhutsjsyeilizru.supabase.co'
const SUPABASE_KEY = 'sb_publishable_-PsHAoJR8NW_mJR5ebnAdA_lhl0u9gN'
const TABLE = 'daily_tickets'
const REST = `${SUPABASE_URL}/rest/v1/${TABLE}`
const CACHE_KEY = 'blackrock_daily_tickets_cache_v1'

const baseHeaders: Record<string, string> = {
  apikey: SUPABASE_KEY,
  Authorization: `Bearer ${SUPABASE_KEY}`,
  'Content-Type': 'application/json',
}

export interface SavedMaterialRow {
  id: string
  material: string
  description: string
  loads: number
}

export interface SavedTicket {
  id: string
  updatedAt: number
  ticketNumber: string
  date: string
  jobName: string
  customer: string
  jobAddress: string
  projectPhoneEmail: string
  billToName: string
  email: string
  phone: string
  timeIn: string
  timeOut: string
  materials: SavedMaterialRow[]
  notes: string
  driverSignature: string
  companyRepSignature: string
}

// A PostgREST row: the full ticket lives in the `data` JSONB column, with `id`
// and `updated_at` mirrored to real columns for keying and ordering.
interface TicketRow {
  id: string
  data: SavedTicket
  updated_at: string
}

function generateId(): string {
  return `tkt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

function readCache(): SavedTicket[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as SavedTicket[]) : []
  } catch {
    return []
  }
}

function writeCache(tickets: SavedTicket[]): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(tickets))
  } catch {
    // Cache is best-effort; ignore quota errors.
  }
}

function sortNewestFirst(tickets: SavedTicket[]): SavedTicket[] {
  return [...tickets].sort((a, b) => b.updatedAt - a.updatedAt)
}

// Last cloud failure reason, so the UI can show WHY it fell back to the cache
// instead of a generic "Offline". Read right after a listTickets() call.
let lastSyncError: string | null = null

/** Human-readable reason the last cloud read failed, or null if it succeeded. */
export function getLastSyncError(): string | null {
  return lastSyncError
}

// Fetch all tickets from Supabase (newest first). On any network/DB error we
// fall back to the offline cache so the UI keeps working. `fromCache` on the
// result lets the caller surface an "offline" hint if desired.
export async function listTickets(): Promise<{
  tickets: SavedTicket[]
  fromCache: boolean
}> {
  try {
    const res = await fetch(
      `${REST}?select=id,data,updated_at&order=updated_at.desc`,
      { headers: baseHeaders },
    )
    if (!res.ok) {
      const detail = await res.text().catch(() => '')
      throw new Error(
        `Supabase returned ${res.status}${detail ? ` — ${detail.slice(0, 160)}` : ''}`,
      )
    }
    const rows = (await res.json()) as TicketRow[]
    const tickets = sortNewestFirst(rows.map((r) => r.data))
    writeCache(tickets)
    lastSyncError = null
    return { tickets, fromCache: false }
  } catch (err) {
    lastSyncError = err instanceof Error ? err.message : String(err)
    console.error('listTickets failed, using cache', err)
    return { tickets: sortNewestFirst(readCache()), fromCache: true }
  }
}

// Insert or update a ticket. Uses PostgREST upsert (merge-duplicates on the id
// primary key). Returns the saved record with its id + updatedAt stamped.
export async function upsertTicket(
  ticket: Omit<SavedTicket, 'id' | 'updatedAt'> & { id?: string },
): Promise<SavedTicket> {
  const id = ticket.id || generateId()
  const now = Date.now()
  const record: SavedTicket = { ...ticket, id, updatedAt: now }
  const row: TicketRow = {
    id,
    data: record,
    updated_at: new Date(now).toISOString(),
  }
  const res = await fetch(REST, {
    method: 'POST',
    headers: {
      ...baseHeaders,
      Prefer: 'resolution=merge-duplicates,return=representation',
    },
    body: JSON.stringify([row]),
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(`Supabase upsert ${res.status} ${detail}`)
  }
  // Keep the offline cache in sync.
  const cached = readCache().filter((t) => t.id !== id)
  writeCache(sortNewestFirst([record, ...cached]))
  return record
}

export async function deleteTicket(id: string): Promise<void> {
  const res = await fetch(`${REST}?id=eq.${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: baseHeaders,
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(`Supabase delete ${res.status} ${detail}`)
  }
  writeCache(readCache().filter((t) => t.id !== id))
}
